import React, { ReactNode, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { Item, Item as Serie } from "@charty/legend/item/model";
import { useTheme } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import Chart from "./chart";
import { useOtherSeriesIds, useSeries } from "./data";
import HorizontalLegend from "./legend/horizontal-legend";
import VerticalLegend from "./legend/vertical-legend";

type ChartTooltip = {
  serie: Item;
  datasetTotal: number;
};

interface IProps {
  series: Serie[];
  locale: Locale;
  onClickPie?: (uid: Serie["id"] | Serie["id"][]) => void;
  onClickLegend?: (uid: Serie["id"] | Serie["id"][]) => void;
  displayTotal?: boolean;
  total?: number;
  orderBy?: "natural" | "numeric";
  centerLabel?: string;
  tooltip?: (serie: Item, total: number) => ReactNode;
  className?: string;
  chartTooltip?: (props: ChartTooltip) => ReactNode;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
}

const Root = styled.div<{ isWide: boolean }>`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: ${(props) => (props.isWide ? "center" : "stretch")};
  flex-direction: ${(props) => (props.isWide ? "row" : "column")};
  margin: ${(props) => props.isWide && "0 auto"};
  overflow: hidden;
`;

const DoughnutChart = ({
  series,
  locale,
  displayTotal = false,
  orderBy = "natural",
  tooltip,
  centerLabel,
  total,
  onClickPie,
  onClickLegend,
  className,
  chartTooltip,
  disableAnimation = false,
  isFullscreen,
}: IProps) => {
  const [hovered, setHovered] = useState(null);
  const ref = useRef(null);
  const { width, height } = useSize(ref);
  const isWide = width > height + 224;
  const theme = useTheme();
  const _series = useSeries(series, locale, theme);
  const otherSeriesId = useOtherSeriesIds(series);

  const _onClickLegend = useCallback(
    (serieId) => {
      if (onClickLegend) {
        if (serieId !== "other") {
          onClickLegend(serieId);
        } else {
          onClickLegend(otherSeriesId);
        }
      }
    },
    [onClickLegend, otherSeriesId]
  );

  const _onClickPie = useCallback(
    (serieId) => {
      if (onClickPie) {
        if (serieId !== "other") {
          onClickPie(serieId);
        } else {
          onClickPie(otherSeriesId);
        }
      }
    },
    [onClickPie, otherSeriesId]
  );

  return (
    <Root ref={ref} isWide={isWide} className={className}>
      {!isWide && (
        <HorizontalLegend
          series={_series}
          total={total}
          locale={locale}
          setHovered={setHovered}
          tooltip={tooltip}
          onClickLegend={onClickLegend && _onClickLegend}
          orderBy={orderBy}
          disableAnimation={disableAnimation}
        />
      )}
      <Chart
        series={_series}
        total={total}
        locale={locale}
        displayTotal={displayTotal}
        orderBy={orderBy}
        centerLabel={centerLabel}
        onClickPie={onClickPie && _onClickPie}
        chartTooltip={chartTooltip}
        hovered={hovered}
        width={width}
        height={height}
        isWide={isWide}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
      />
      {isWide && (
        <VerticalLegend
          series={_series}
          total={total}
          locale={locale}
          setHovered={setHovered}
          tooltip={tooltip}
          onClickLegend={onClickLegend && _onClickLegend}
          orderBy={orderBy}
          disableAnimation={disableAnimation}
        />
      )}
    </Root>
  );
};

export default DoughnutChart;
